import { useState, useCallback } from 'react';

/**
 * A simplified localStorage hook that works reliably in all environments
 * without the complexity of live binding across tabs.
 *
 * @param key - The localStorage key to use
 * @param initialValue - Default value if no value exists in localStorage
 * @returns [storedValue, setValue] - Current value and setter function
 */
export function useSimpleLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((val: T) => T)) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);

      if (item === null) {
        return initialValue;
      }

      try {
        return JSON.parse(item);
      } catch {
        return item as unknown as T;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(
        `Fenrir: Failed to get item: ${key} from localStorage. Error: ${error}`,
      );
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value: T | ((val: T) => T)) => {
      try {
        const valueToStore =
          typeof value === 'function'
            ? (value as (val: T) => T)(storedValue)
            : value;

        setStoredValue(valueToStore);

        if (
          typeof window === 'undefined' ||
          window.localStorage === undefined
        ) {
          return;
        }

        if (valueToStore === undefined) {
          window.localStorage.removeItem(key);
        } else if (typeof valueToStore === 'string') {
          window.localStorage.setItem(key, valueToStore);
        } else {
          window.localStorage.setItem(key, JSON.stringify(valueToStore));
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(
          `Fenrir: Failed to set item: ${key} to localStorage. Error: ${error}`,
        );
      }
    },
    [key, storedValue],
  );

  return [storedValue, setValue];
}
